import { useState } from 'react';
import FormField from '@/shared/components/FormField';
import { CategoryPicker } from '@/features/categories';
import { S } from '@/shared/constants/styles';
import { PAYMENT_METHODS } from '@/shared/constants/config';

function TagInput({ value, onChange, theme: t, isDark }) {
  const [input, setInput] = useState('');

  const addTag = () => {
    const tag = input.trim().toLowerCase();
    if (!tag || value.includes(tag)) { setInput(''); return; }
    onChange([...value, tag]);
    setInput('');
  };

  const removeTag = (tag) => onChange(value.filter(tg => tg !== tag));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
    if (e.key === 'Backspace' && !input && value.length > 0) removeTag(value[value.length - 1]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addTag}
        placeholder="Type a tag and press Enter"
        style={{ ...S.input, fontSize: 13 }}
      />
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {value.map(tag => (
            <span key={tag} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: isDark ? 'rgba(99,102,241,0.2)' : '#e0e7ff',
              color: isDark ? '#a5b4fc' : '#3730a3',
              border: '1px solid ' + (isDark ? 'rgba(99,102,241,0.4)' : '#c7d2fe'),
              borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
            }}>
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit', opacity: 0.7, fontSize: 13 }}
              >×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TransactionForm({
  txForm, setTxForm, txErrors, setTxErrors,
  editId, saveTx, onClose,
  incomeList, allSubCats, getGroup,
  resolvedMemberNames,
  theme: t, isDark,
}) {
  const isIncomeType = txForm.type === "income";
  const categoryOptions = (isIncomeType ? [...incomeList] : [...allSubCats]).sort((a, b) => a.localeCompare(b));

  return (
    <>
      {Object.values(txErrors).filter(Boolean).length > 0 && (
        <div style={{ background: t.errorBg, border: "1px solid " + t.errorBorder, borderRadius: 10, padding: "10px 14px", marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.errorText }}>Please fix the following:</div>
          {Object.values(txErrors).filter(Boolean).map((e, i) => (
            <div key={i} style={{ fontSize: 12, color: t.errorText, marginTop: 4 }}>{"• " + e}</div>
          ))}
        </div>
      )}
      <FormField label="Date">
        <input
          type="date" value={txForm.date}
          onChange={e => { setTxForm({ ...txForm, date: e.target.value }); setTxErrors(er => { const n = { ...er }; delete n.date; return n; }); }}
          style={{ ...S.input, borderColor: txErrors.date ? "#fca5a5" : undefined }}
        />
      </FormField>
      <FormField label="Description *">
        <input
          value={txForm.description}
          onChange={e => { setTxForm({ ...txForm, description: e.target.value }); setTxErrors(er => { const n = { ...er }; delete n.description; return n; }); }}
          placeholder="e.g. Coles groceries"
          style={{ ...S.input, borderColor: txErrors.description ? "#fca5a5" : undefined }}
        />
      </FormField>
      <FormField label="Type">
        <select
          value={txForm.type}
          onChange={e => {
            const type = e.target.value;
            setTxForm({ ...txForm, type, category: type === "income" ? (incomeList[0] || "") : (allSubCats[0] || "") });
          }}
          style={S.select}
        >
          <option value="expense">Expense</option>
          <option value="income">Income</option>
        </select>
      </FormField>
      <FormField label="Category *">
        <CategoryPicker
          value={txForm.category}
          onChange={c => { setTxForm({ ...txForm, category: c }); setTxErrors(er => { const n = { ...er }; delete n.category; return n; }); }}
          options={categoryOptions}
          getGroup={getGroup}
          theme={t}
          isDark={isDark}
          error={!!txErrors.category}
        />
      </FormField>
      <FormField label="Amount (AUD) *">
        <input
          type="number" step="0.01" value={txForm.amount}
          onChange={e => { setTxForm({ ...txForm, amount: e.target.value }); setTxErrors(er => { const n = { ...er }; delete n.amount; return n; }); }}
          placeholder="0.00"
          style={{ ...S.input, borderColor: txErrors.amount ? "#fca5a5" : undefined }}
        />
      </FormField>
      <FormField label="Who">
        <select value={txForm.who} onChange={e => setTxForm({ ...txForm, who: e.target.value })} style={S.select}>
          {resolvedMemberNames.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </FormField>
      <FormField label="Payment Method">
        <select value={txForm.paymentMethod} onChange={e => setTxForm({ ...txForm, paymentMethod: e.target.value })} style={S.select}>
          {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </FormField>
      <FormField label="Notes (optional)">
        <input value={txForm.notes} onChange={e => setTxForm({ ...txForm, notes: e.target.value })} placeholder="Any extra detail" style={S.input} />
      </FormField>
      <FormField label="Tags (optional)">
        <TagInput
          value={txForm.tags || []}
          onChange={tags => setTxForm({ ...txForm, tags })}
          theme={t}
          isDark={isDark}
        />
      </FormField>
      <button onClick={saveTx} style={{ ...S.btn(), width: "100%", padding: 13, borderRadius: 10, fontSize: 14, marginTop: 4 }}>
        {editId ? "Save Changes" : "Add Transaction"}
      </button>
    </>
  );
}
